import { useQuery } from '@tanstack/react-query';
import { fetchApi } from '@/api/client';
import type { BridgeRoutesResponse, TokenMappingResponse } from '@/api/types';
import { CHAIN_ID, EXPLORER_BASE } from '@/config';
import bridgeRoutesFallback from '@repo-config/bridge-routes-chain138-default.json';

const PAIRS: Array<{ from: number; to: number; label: string }> = [
  { from: CHAIN_ID, to: 1, label: `${CHAIN_ID} → Ethereum` },
  { from: 1, to: CHAIN_ID, label: `Ethereum → ${CHAIN_ID}` },
  { from: CHAIN_ID, to: 651940, label: `${CHAIN_ID} → ALL (651940)` },
];

export function RoutingPage() {
  const routesQ = useQuery({
    queryKey: ['bridge-routes'],
    queryFn: async (): Promise<BridgeRoutesResponse> => {
      try {
        return await fetchApi<BridgeRoutesResponse>('/api/v1/bridge/routes');
      } catch {
        return bridgeRoutesFallback as BridgeRoutesResponse;
      }
    },
    staleTime: 120_000,
  });

  const mappingQs = useQuery({
    queryKey: ['token-mappings', PAIRS],
    queryFn: async () => {
      const out: Array<{ label: string; data: TokenMappingResponse | null; error?: string }> = [];
      for (const p of PAIRS) {
        try {
          const data = await fetchApi<TokenMappingResponse>(
            `/api/v1/token-mapping?fromChain=${p.from}&toChain=${p.to}`,
          );
          out.push({ label: p.label, data });
        } catch (e) {
          out.push({
            label: p.label,
            data: null,
            error: (e as Error).message,
          });
        }
      }
      return out;
    },
    staleTime: 120_000,
  });

  return (
    <>
      <h1>Routing &amp; bridges</h1>
      <p className="lead">
        CCIP WETH9/WETH10 bridge addresses and cross-chain token mapping for building swap → bridge →
        swap flows. Single-hop quotes remain on the <a href="/swap">Swap</a> tab.
      </p>

      <h2>Chain {CHAIN_ID} bridge contracts</h2>
      {routesQ.isError && <div className="err-box">{(routesQ.error as Error).message}</div>}
      <p className="muted" style={{ marginBottom: '0.75rem' }}>
        Bridge table uses the live API when any configured host returns 200; if all bases fail, the UI
        falls back to <span className="mono">config/bridge-routes-chain138-default.json</span>.
      </p>
      {routesQ.data?.chain138Bridges && (
        <div className="table-wrap">
          <table className="data">
            <tbody>
              <tr>
                <th>WETH9 bridge (138)</th>
                <td className="mono">
                  <a
                    href={`${EXPLORER_BASE}/address/${routesQ.data.chain138Bridges.weth9}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {routesQ.data.chain138Bridges.weth9}
                  </a>
                </td>
              </tr>
              <tr>
                <th>WETH10 bridge (138)</th>
                <td className="mono">
                  <a
                    href={`${EXPLORER_BASE}/address/${routesQ.data.chain138Bridges.weth10}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {routesQ.data.chain138Bridges.weth10}
                  </a>
                </td>
              </tr>
              {routesQ.data.chain138Bridges.trustless && (
                <tr>
                  <th>Trustless Lockbox (138)</th>
                  <td className="mono">
                    <a
                      href={`${EXPLORER_BASE}/address/${routesQ.data.chain138Bridges.trustless}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {routesQ.data.chain138Bridges.trustless}
                    </a>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <h2>Destination routers (WETH9)</h2>
      <p className="muted" style={{ marginBottom: '0.75rem' }}>
        Ethereum mainnet shows the relay release bridge for 138→1 WETH9 (
        <span className="mono">0xF9A32F37099c582D28b4dE7Fca6eaC1e5259f939</span>). CCIP hub{' '}
        <span className="mono">0xc9901ce2Ddb6490FAA183645147a87496d8b20B6</span> remains the on-chain CCIP receiver.
      </p>
      {routesQ.data?.routes?.weth9 && (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Destination</th>
                <th>Bridge / router address</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(routesQ.data.routes.weth9).map(([name, addr]) => (
                <tr key={name}>
                  <td>{name}</td>
                  <td className="mono">{addr}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2>Destination routers (WETH10)</h2>
      {routesQ.data?.routes?.weth10 && (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Destination</th>
                <th>Bridge / router address</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(routesQ.data.routes.weth10).map(([name, addr]) => (
                <tr key={name}>
                  <td>{name}</td>
                  <td className="mono">{addr}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2>Token mapping snapshots</h2>
      {mappingQs.isFetching && <p className="muted">Loading mappings…</p>}
      {mappingQs.data?.map((block) => (
        <div key={block.label} style={{ marginBottom: '1.25rem' }}>
          <h3 style={{ fontSize: '1rem', marginBottom: '0.35rem' }}>{block.label}</h3>
          {block.error && <div className="err-box">{block.error}</div>}
          {block.data?.tokens && (
            <p className="muted mono" style={{ fontSize: '0.8rem' }}>
              {block.data.tokens.slice(0, 24).join(', ')}
              {block.data.tokens.length > 24 ? '…' : ''}
            </p>
          )}
        </div>
      ))}
    </>
  );
}
